import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../contexts/AuthContext';

export const useFavorites = () => {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadFavorites();
    } else {
      setFavorites(new Set());
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadFavorites = async () => {
    if (!user || !user.id) {
      setFavorites(new Set());
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('user_favorites')
        .select('event_id')
        .eq('user_id', user.id);

      if (error) throw error;

      // Filter out null/undefined event_ids and invalid values
      const favoriteIds = new Set(
        data
          ?.map(f => f.event_id)
          .filter(id => id && id !== 'null' && id !== 'undefined') || []
      );
      setFavorites(favoriteIds);
    } catch (error) {
      console.error('Error loading favorites:', error);
      setFavorites(new Set());
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async (
    itemId: string,
    itemType: 'event' | 'camp' | 'venue' | 'attraction' | 'class' | 'school_district' = 'event'
  ): Promise<boolean> => {
    // Validate user and user ID
    if (!user || !user.id || user.id === 'null' || user.id === 'undefined') {
      console.error('Invalid user state:', { user: user?.id });
      return false;
    }

    // Validate itemId
    if (!itemId || itemId === 'null' || itemId === 'undefined' || itemId.trim() === '') {
      console.error('Invalid itemId:', itemId);
      alert('Cannot save favorite: Invalid item ID');
      return false;
    }

    const isFavorited = favorites.has(itemId);

    try {
      if (isFavorited) {
        // Remove from favorites - use the specific column for the item type
        const columnName = itemType === 'event' ? 'event_id' :
          itemType === 'camp' ? 'camp_id' :
          itemType === 'venue' ? 'venue_id' :
          itemType === 'attraction' ? 'attraction_id' :
          itemType === 'class' ? 'class_id' :
          'school_district_id';

        const { error } = await supabase
          .from('user_favorites')
          .delete()
          .eq('user_id', user.id)
          .eq(columnName, itemId);

        if (error) throw error;

        setFavorites(prev => {
          const newSet = new Set(prev);
          newSet.delete(itemId);
          return newSet;
        });
      } else {
        // Add to favorites - only insert the specific column for this item type
        const favoriteData: any = {
          user_id: user.id,
        };

        // Set only the specific column for this item type
        if (itemType === 'event') favoriteData.event_id = itemId;
        else if (itemType === 'camp') favoriteData.camp_id = itemId;
        else if (itemType === 'venue') favoriteData.venue_id = itemId;
        else if (itemType === 'attraction') favoriteData.attraction_id = itemId;
        else if (itemType === 'class') favoriteData.class_id = itemId;
        else if (itemType === 'school_district') favoriteData.school_district_id = itemId;

        // Log the data being sent (for debugging)
        console.log('Inserting favorite:', {
          itemType,
          itemId,
          user_id: user.id,
          favoriteData
        });

        const { error } = await supabase
          .from('user_favorites')
          .insert(favoriteData);

        if (error) {
          console.error('Database error:', error);
          console.error('Attempted insert:', favoriteData);
          throw error;
        }

        setFavorites(prev => new Set(prev).add(itemId));
      }

      return !isFavorited; // Return new state
    } catch (error) {
      console.error('Error toggling favorite:', error);
      alert('Failed to update favorite. Please try again.');
      return isFavorited; // Return original state on error
    }
  };

  const isFavorited = (eventId: string): boolean => {
    return favorites.has(eventId);
  };

  return {
    favorites,
    loading,
    toggleFavorite,
    isFavorited,
  };
};
